import {
  SET_SELECTED_LOCATION_ID,
  SET_LOCATION_VIEW_STYLE,
  SET_CURRENT_MAP_ID,
  ADD_NEW_SAMPLE,
  RECEIVE_PROTOCOL,
  REQUEST_SET_LOCATION,
  RECEIVE_SET_LOCATION,
  SET_AVERAGE_SHEAR_WAVE_SPEED,
  SET_AVERAGE_THICKNESS,
  INCREASE_SAMPLES_PROCESSED,
  INCLUDE_SAMPLE,
  SET_NEW_LOCATION,
  EXCLUDE_SAMPLE,
  RECEIVE_SET_STUDY,
  RESET_REDUX,
  SET_SELECTOR_POSITION,
  SET_NEXT_AUTO_STEP,
  SET_LOCATION_ALERT,
  SET_LAST_VIEWED_SAMPLE,
  INCREASE_SAMPLES_ACQUIRED,
  SET_CURRENT_ACQ_TYPE,
  REMOVE_ERRORED_SAMPLE,
  REMOVE_ACQ_SAMPLE,
  SET_LOCATION_WIDE_ROI_REFERENCE,
  SET_DEFAULT_ROI_REFERENCE,
  CANCEL_PHOTO,
  ADD_TEMP_LOCATION,
  REMOVE_TEMP_LOCATION,
  RECEIVE_FACE_DETECTION,
  MOVE_SAMPLE,
} from "../actions/index";

import {
  tempLocationId,
  tempLocationSymmetricalId,
  customFaceMapId,
  csiTypes,
} from "../constants/constants";

import produce from "immer";

const initialState = {
  selectorPosition: 126,
  selectedLocationId: null,
  byId: {},
  allIds: [],
  currentAcqType: null,
  lastSetLocation: { location: null, acqType: null },
  nextAutoStep: { form: null, locationId: null, acqType: null },
  currentLocationView: "LIST", // LIST, BODY_MAP
};

export default (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case RECEIVE_PROTOCOL: {
        //Map acq types into a dict
        let acqTypeDict = {};
        for (let type in Object.keys(action.setProtocol.acq_types)) {
          acqTypeDict[Object.keys(action.setProtocol.acq_types)[type]] = 0;
        }

        // Map protocol locations
        let locationById = {};
        let protocolLocations = action.setProtocol.locations;
        Object.keys(protocolLocations).forEach((location) => {
          locationById[location] = {};
          locationById[location].id = location;
          locationById[location].name = protocolLocations[location].name;
          locationById[location].samplesNeeded =
            protocolLocations[location].samples_needed;
          // Add default values to each location
          locationById[location].samplesAcquired = acqTypeDict;
          locationById[location].samplesProcessed = acqTypeDict;
          locationById[location].samplesExcluded = acqTypeDict;
          locationById[location].completed = false;
          locationById[location].alert = {
            status: false,
            sampleIncludedInStudy: false,
          };
          locationById[location].shearWaveSpeed = {
            value: null,
            standardDev: null,
          };
          locationById[location].thickness = {
            value: null,
            standardDev: null,
          };
          locationById[location].roiReference = {
            locationWide: { id: null, type: null },
            default: null,
          };
          locationById[location].samples = [];
          return locationById;
        });

        draft.byId = locationById;
        draft.allIds = Object.keys(action.setProtocol.locations);

        // Maps
        if (Object.keys(action.setProtocol.maps).includes(customFaceMapId)) {
          draft.currentMapId = customFaceMapId;
        } else {
          draft.currentMapId = Object.keys(action.setProtocol.maps)[0]; // Default "selected Bodymap"
        }

        // UI Elements
        if (Object.keys(action.setProtocol.locations).length !== 0) {
          draft.currentLocationView = "LIST";
          draft.selectedLocationId = Object.keys(
            action.setProtocol.locations
          )[0]; // Default "selected location"
        } else {
          draft.currentLocationView = "BODY_MAP";
        }
        draft.currentAcqType = Object.keys(
          action.setProtocol.acq_types
        ).includes("BMODE")
          ? "BMODE"
          : Object.keys(action.setProtocol.acq_types)[0]; //Default acq type
        break;
      }

      case RECEIVE_FACE_DETECTION: {
        // Add new map to body maps
        draft.currentMapId = customFaceMapId;
        break;
      }

      // remove currently selected sample from location
      case CANCEL_PHOTO: {
        draft.byId[draft.selectedLocationId].samples = draft.byId[
          draft.selectedLocationId
        ].samples.filter((id) => {
          return id !== action.sampleId;
        });
        break;
      }

      case RECEIVE_SET_STUDY: {
        draft.byId = action.locations.byId;
        break;
      }

      case SET_SELECTED_LOCATION_ID: {
        draft.selectedLocationId = action.locationId;
        break;
      }

      case SET_LOCATION_VIEW_STYLE: {
        draft.currentLocationView = action.view;
        break;
      }

      case SET_CURRENT_MAP_ID: {
        draft.currentMapId = action.id;
        break;
      }

      case MOVE_SAMPLE: {
        // 1) Remove sample from the current location's sample list
        let newSampleList = draft.byId[action.currentLocationId].samples.filter(
          (sampleId) => sampleId !== action.sampleId
        );
        draft.byId[action.currentLocationId].samples = newSampleList;
        // 2) Add sample Id to new location's sample list
        draft.byId[action.newLocationId].samples.push(action.sampleId);
        // 3) Reset lasViewSampleId
        draft.byId[action.newLocationId].lastViewedSample = null;
        // 4) Reset ROI references
        if (
          draft.byId[action.currentLocationId].roiReference.default ===
          action.sampleId
        ) {
          draft.byId[action.currentLocationId].roiReference.default = null;
        }
        if (
          draft.byId[action.currentLocationId].roiReference.locationWide.id ===
          action.sampleId
        ) {
          draft.byId[action.currentLocationId].roiReference.locationWide.id =
            null;
        }
        // 5) Increment/decrement samplesAcquired and samplesProcessed
        draft.byId[action.currentLocationId].samplesAcquired[
          action.sampleType
        ] -= 1;
        draft.byId[action.currentLocationId].samplesProcessed[
          action.sampleType
        ] -= 1;
        draft.byId[action.currentLocationId].completed =
          action.currentLocationCompleted;

        draft.byId[action.newLocationId].samplesAcquired[
          action.sampleType
        ] += 1;
        draft.byId[action.newLocationId].samplesProcessed[
          action.sampleType
        ] += 1;
        draft.byId[action.newLocationId].completed =
          action.newLocationCompleted;
        break;
      }

      // add sample to location
      case ADD_NEW_SAMPLE: {
        draft.byId[action.locationId].samples.push(action.id);
        break;
      }

      case INCREASE_SAMPLES_ACQUIRED: {
        let currentLocation = draft.byId[action.id];
        let totalSamplesAcquired = 0;
        for (let acqType in action.allAcqTypes) {
          let samplesAcquired = currentLocation.samplesAcquired[acqType];
          const samplesNeeded = currentLocation.samplesNeeded[acqType];
          if (acqType === action.acqType) {
            samplesAcquired += 1;
          }
          if (samplesAcquired < samplesNeeded) {
            totalSamplesAcquired += samplesAcquired;
          } else {
            totalSamplesAcquired += samplesNeeded;
          }
        }

        draft.byId[action.id].samplesAcquired[action.acqType] =
          draft.byId[action.id].samplesAcquired[action.acqType] + 1;
        draft.byId[action.id].completed =
          totalSamplesAcquired >=
          Object.values(currentLocation.samplesNeeded).reduce((a, b) => a + b);
        break;
      }

      case INCREASE_SAMPLES_PROCESSED: {
        draft.byId[action.id].samplesProcessed[action.acqType] =
          draft.byId[action.id].samplesProcessed[action.acqType] + 1;
        break;
      }

      case REQUEST_SET_LOCATION: {
        draft.setLocation = false;
        break;
      }

      case RECEIVE_SET_LOCATION: {
        draft.setLocation = true;
        draft.lastSetLocation.location = action.locationId;
        draft.lastSetLocation.acqType = action.acqType;
        break;
      }

      case SET_AVERAGE_SHEAR_WAVE_SPEED: {
        draft.byId[action.locationId].shearWaveSpeed.value =
          action.newShearWaveSpeed;
        draft.byId[action.locationId].shearWaveSpeed.standardDev =
          action.newStandardDev;
        break;
      }

      // if the error occurred during initial view processing, samplesAcquired needs to be decremented
      case REMOVE_ERRORED_SAMPLE: {
        let totalSamplesNeeded = 0;
        let totalSamplesAcquired = 0;
        for (let type in action.allAcqTypes) {
          //add acquired samples based on acquisition type
          const samplesNeeded =
            draft.byId[action.locationId].samplesNeeded[type];
          const samplesAcquired =
            draft.byId[action.locationId].samplesAcquired[type];

          totalSamplesNeeded += samplesNeeded;
          if (samplesAcquired < samplesNeeded) {
            totalSamplesAcquired += samplesAcquired;
          } else {
            totalSamplesAcquired += samplesNeeded;
          }
        }
        totalSamplesAcquired -= 1;

        draft.byId[action.locationId].samples = action.locationSamples;
        draft.byId[action.locationId].samplesAcquired[action.acqType] -= 1;
        draft.byId[action.locationId].completed =
          totalSamplesAcquired >= totalSamplesNeeded;
        break;
      }

      // if the error occurred during acquisition, samplesAcquired has not yet been iterated
      case REMOVE_ACQ_SAMPLE: {
        draft.byId[action.locationId].samples = action.locationSamples;
        break;
      }

      case SET_AVERAGE_THICKNESS: {
        draft.byId[action.locationId].thickness.value = action.newThickness;
        draft.byId[action.locationId].thickness.standardDev =
          action.newStandardDev;
        break;
      }

      case INCLUDE_SAMPLE: {
        let location = draft.byId[action.locationId];
        let inputAcqType = null;
        if (csiTypes.includes(action.acqType)) {
          inputAcqType = "CSI";
        } else if (action.acqType === "BMODE") {
          inputAcqType = "BMODE";
        } else if (action.acqType === "PICTURE") {
          inputAcqType = "PICTURE";
        }

        let totalSamplesNeeded = 0;
        let includetotalSamplesAcquired = 0;
        for (let type in action.allAcqTypes) {
          //add acquired samples based on acquisition type
          const samplesNeeded = location.samplesNeeded[type];
          let samplesAcquired = location.samplesAcquired[type];

          if (type === inputAcqType) {
            samplesAcquired += 1;
          }

          totalSamplesNeeded += samplesNeeded;
          if (samplesAcquired < samplesNeeded) {
            includetotalSamplesAcquired += samplesAcquired;
          } else {
            includetotalSamplesAcquired += samplesNeeded;
          }
        }

        draft.byId[action.locationId].samplesAcquired[inputAcqType] =
          location.samplesAcquired[inputAcqType] + 1;
        draft.byId[action.locationId].samplesProcessed[inputAcqType] =
          location.samplesProcessed[inputAcqType] + 1;
        draft.byId[action.locationId].samplesExcluded[inputAcqType] =
          location.samplesExcluded[inputAcqType] - 1;
        draft.byId[action.locationId].completed =
          includetotalSamplesAcquired >= totalSamplesNeeded;
        break;
      }

      case EXCLUDE_SAMPLE: {
        let selectedlocation = draft.byId[action.locationId];
        let chosenInputAcqType = null;
        if (csiTypes.includes(action.acqType)) {
          chosenInputAcqType = "CSI";
        } else if (action.acqType === "BMODE") {
          chosenInputAcqType = "BMODE";
        } else if (action.acqType === "PICTURE") {
          chosenInputAcqType = "PICTURE";
        }

        let excludeTotalSamplesNeeded = 0;
        let excludeTotalSamplesAcquired = 0;
        for (let type in action.allAcqTypes) {
          //add acquired samples based on acquisition type
          const samplesNeeded = selectedlocation.samplesNeeded[type];
          let samplesAcquired = selectedlocation.samplesAcquired[type];
          if (type === chosenInputAcqType) {
            samplesAcquired = samplesAcquired - 1;
          }
          excludeTotalSamplesNeeded += samplesNeeded;
          if (samplesAcquired < samplesNeeded) {
            excludeTotalSamplesAcquired += samplesAcquired;
          } else {
            excludeTotalSamplesAcquired += samplesNeeded;
          }
        }

        draft.byId[action.locationId].samplesAcquired[chosenInputAcqType] =
          selectedlocation.samplesAcquired[chosenInputAcqType] - 1;
        draft.byId[action.locationId].samplesProcessed[chosenInputAcqType] =
          selectedlocation.samplesProcessed[chosenInputAcqType] - 1;
        draft.byId[action.locationId].samplesExcluded[chosenInputAcqType] =
          selectedlocation.samplesExcluded[chosenInputAcqType] + 1;
        draft.byId[action.locationId].completed =
          excludeTotalSamplesAcquired >= excludeTotalSamplesNeeded;
        break;
      }

      case SET_SELECTOR_POSITION: {
        draft.selectorPosition = action.position;
        break;
      }

      case SET_NEXT_AUTO_STEP: {
        draft.nextAutoStep.form = action.form;
        draft.nextAutoStep.locationId = action.locationId;
        draft.nextAutoStep.acqType = action.acqType;
        break;
      }

      case SET_LOCATION_ALERT: {
        draft.byId[action.locationId].alert.status = action.alert;
        draft.byId[action.locationId].alert.sampleIncludedInStudy =
          action.sampleIncludedInStudy;
        break;
      }

      case SET_LAST_VIEWED_SAMPLE: {
        draft.byId[action.locationId].lastViewedSample = action.sampleId;
        break;
      }

      case SET_CURRENT_ACQ_TYPE: {
        draft.currentAcqType = action.acqType;
        break;
      }

      case SET_LOCATION_WIDE_ROI_REFERENCE: {
        //ROI reference which sets the depth range for all CSI samples in that location //A single bmode in each location can be used as an
        draft.byId[action.locationId].roiReference.locationWide.id =
          action.sampleId;
        draft.byId[action.locationId].roiReference.locationWide.type =
          action.sampleType;
        break;
      }

      case SET_DEFAULT_ROI_REFERENCE: {
        //ROI reference that sets the default depth range for new samples
        draft.byId[action.locationId].roiReference.default = action.sampleId;
        break;
      }

      case ADD_TEMP_LOCATION: {
        if (draft.allIds.includes(tempLocationId) && action.symmetry) {
          draft.byId[tempLocationSymmetricalId] = {
            ...draft.byId[tempLocationId],
            id: tempLocationSymmetricalId,
            name: "Temporary Location Symmetrical",
          };
          draft.allIds.push(tempLocationSymmetricalId);
        } else {
          draft.byId[tempLocationId] = {
            id: tempLocationId,
            name: "Temporary Location",
            samplesNeeded: 0,
            samplesAcquired: {},
            samplesProcessed: {},
            samplesExcluded: {},
            completed: false,
            roiReference: {
              locationWide: { id: null, type: null },
              default: null,
            },
            alert: { status: false, sampleIncludedInStudy: false },
            shearWaveSpeed: { value: null, standardDev: null },
            thickness: { value: null, standardDev: null },
            samples: [],
          };
          draft.allIds.push(tempLocationId);
        }
        break;
      }

      case REMOVE_TEMP_LOCATION: {
        if (
          draft.allIds.includes(tempLocationSymmetricalId) &&
          action.symmetry
        ) {
          // Only remove temp location symmetrical
          delete draft.byId[tempLocationSymmetricalId];
          if (draft.allIds.indexOf(tempLocationSymmetricalId) > -1) {
            draft.allIds.splice(
              draft.allIds.indexOf(tempLocationSymmetricalId),
              1
            );
          }
        } else if (
          draft.allIds.includes(tempLocationSymmetricalId) &&
          !action.symmetry
        ) {
          // Remove both temp location AND temp location symmetrical
          delete draft.byId[tempLocationId];
          delete draft.byId[tempLocationSymmetricalId];
          if (draft.allIds.indexOf(tempLocationId) > -1) {
            draft.allIds.splice(draft.allIds.indexOf(tempLocationId), 1);
          }
          if (draft.allIds.indexOf(tempLocationSymmetricalId) > -1) {
            draft.allIds.splice(
              draft.allIds.indexOf(tempLocationSymmetricalId),
              1
            );
          }
        } else {
          // Only remove temp location
          delete draft.byId[tempLocationId];
          if (draft.allIds.indexOf(tempLocationId) > -1) {
            draft.allIds.splice(draft.allIds.indexOf(tempLocationId), 1);
          }
        }
        break;
      }

      case SET_NEW_LOCATION: {
        Object.keys(action.newLocations).forEach((newLocId) => {
          let acqDict = {};
          for (let type in action.newLocations[newLocId].samplesNeeded) {
            acqDict[type] = 0;
          }

          draft.byId[action.newLocations[newLocId].locationId] = {
            id: action.newLocations[newLocId].locationId,
            name: action.newLocations[newLocId].locationName,
            samplesNeeded: action.newLocations[newLocId].samplesNeeded,
            samplesAcquired: acqDict,
            samplesProcessed: acqDict,
            samplesExcluded: acqDict,
            completed: false,
            // removed: false,
            roiReference: {
              locationWide: { id: null, type: null },
              default: null,
            },
            alert: { status: false, sampleIncludedInStudy: false },
            shearWaveSpeed: { value: null, standardDev: null },
            thickness: { value: null, standardDev: null },
            samples: [],
          };
          draft.allIds.push(action.newLocations[newLocId].locationId);
          draft.selectedLocationId =
            draft.allIds.length === 1
              ? action.newLocations[newLocId].locationId
              : draft.selectedLocationId;
        });

        break;
      }

      case RESET_REDUX:
        return initialState;

      default:
        return draft;
    }
  });
